import React, { useState, useRef, useCallback } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { SpeakRepeatInteraction } from '../../types/page';
import { isSpeakRepeatInteraction } from '../../types/page';
import { Button } from '../../../../components/ui/Button';
import { Mic, Square, Play } from 'lucide-react';

interface SpeakRepeatV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

export const SpeakRepeatV1: React.FC<SpeakRepeatV1Props> = ({ page, onComplete }) => {
  const interactions = page.page_json_v1?.interactions ?? [];
  const sr = interactions.find(isSpeakRepeatInteraction) as SpeakRepeatInteraction | undefined;

  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  if (!sr) {
    return (
      <div className="p-6 text-gray-500 dark:text-gray-400">
        No speak repeat interaction found.
      </div>
    );
  }

  const targetText = sr.target_text ?? '';

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (chunksRef.current.length) {
          setAudioBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
        }
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    }
    setRecording(false);
  }, []);

  const playRecording = useCallback(() => {
    if (!audioBlob) return;
    const url = URL.createObjectURL(audioBlob);
    const a = new Audio(url);
    a.onended = () => URL.revokeObjectURL(url);
    a.play();
  }, [audioBlob]);

  const handleMarkComplete = () => {
    onComplete?.();
  };

  return (
    <div className="space-y-6 p-6 overflow-auto">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || 'Speak & Repeat'}
      </h1>
      <p className="text-gray-700 dark:text-gray-300">{sr.prompt}</p>
      <p className="text-2xl font-semibold text-gray-900 dark:text-white">{targetText}</p>

      <div className="flex flex-wrap gap-3">
        {recording ? (
          <Button
            variant="outline"
            onClick={stopRecording}
            className="flex items-center gap-2 text-red-600 border-red-500"
          >
            <Square className="w-5 h-5" />
            Stop Recording
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={startRecording}
            className="flex items-center gap-2"
          >
            <Mic className="w-5 h-5" />
            Record
          </Button>
        )}
        {audioBlob && (
          <Button
            variant="outline"
            onClick={playRecording}
            className="flex items-center gap-2"
          >
            <Play className="w-5 h-5" />
            Playback
          </Button>
        )}
      </div>
    </div>
  );
};
